import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Chip, Button, IconButton, Paper, Avatar, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
  List, ListItem, ListItemIcon, ListItemText, ListItemAvatar, Switch,
  Badge, alpha, Divider, Tooltip, Slide, Zoom, Grow, Snackbar,
  LinearProgress, Skeleton, Collapse, AlertTitle
} from '@mui/material';
import {
  Users, ArrowLeft, MessageCircle, HelpCircle, Settings, PlusCircle,
  Check, AlertTriangle, Clock, Shield, Wifi, Globe, Video, Phone,
  Upload, Download, Radio, Headphones, Zap, Send, ExternalLink,
  Bell, Calendar, Crown, Share2, Smartphone, Sparkles, Flame,
  Command, Sliders, VolumeX, Volume, Mic, Star, TrendingUp,
  Activity, BarChart3, Layers, Eye, EyeOff, Lock, Unlock,
  MoreVertical, Copy, Edit, Trash2, RefreshCw, PlayCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ShareCircleButton from '../ShareCircleButton/EnhancedShareCircleButton';

// Import services for dynamic functionality
import { circleService } from '../../services/circleService';
import apiClient, { withAuth } from '../../services/api';
// Use Auth0 instead of non-existent AuthContext
import { useAuth0 } from '@auth0/auth0-react';

/**
 * PRIVACY CONFIGURATION
 * Change this constant to adjust privacy levels across the entire application
 */
const PRIVACY_CONFIG = {
  // Privacy levels: 'initials', 'first_name', 'full_name', 'anonymous'
  defaultLevel: 'initials',
  
  // Whether to show different privacy for owners vs members
  showOwnerTitle: true, // true = "Circle Owner", false = initials like everyone else
  
  // Whether current user always sees "You" regardless of privacy level
  showYouLabel: true,
  
  // Fallback for members without names
  unknownMemberLabel: 'Member',
  
  // Whether to use initials in avatars too
  useInitialsInAvatars: true
};

/**
 * Convert full name to initials for privacy
 * Enhanced version with better edge case handling
 */
const getInitials = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return 'U.M.'; // Unknown Member
  }

  const name = fullName.trim();
  if (!name) return 'U.M.';

  // Handle email addresses (common edge case)
  if (name.includes('@')) {
    const emailName = name.split('@')[0];
    return getInitials(emailName.replace(/[._\d]/g, ' '));
  }

  const nameParts = name.split(/\s+/).filter(part => part.length > 0);
  
  if (nameParts.length === 0) {
    return 'U.M.';
  } else if (nameParts.length === 1) {
    // Single name - use first letter twice or first two characters
    const singleName = nameParts[0];
    if (singleName.length >= 2) {
      return `${singleName.charAt(0).toUpperCase()}.${singleName.charAt(1).toUpperCase()}.`;
    } else {
      return `${singleName.charAt(0).toUpperCase()}.M.`;
    }
  } else {
    // Multiple names - first and last
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}.${lastInitial}.`;
  }
};

/**
 * Enhanced privacy-aware display name function
 * @param {Object} member - Member object
 * @param {string} overrideLevel - Optional privacy level override
 * @returns {string} Display name based on privacy settings
 */
const getMemberDisplayName = (member, overrideLevel = null) => {
  const privacyLevel = overrideLevel || PRIVACY_CONFIG.defaultLevel;
  
  // Always show "You" for current user (if enabled)
  if (PRIVACY_CONFIG.showYouLabel && member.isCurrentUser) {
    return 'You';
  }
  
  // Show "Circle Owner" for account owner (if enabled)
  if (PRIVACY_CONFIG.showOwnerTitle && member.isAccountOwner) {
    return 'Circle Owner';
  }
  
  // Apply privacy level
  switch (privacyLevel) {
    case 'full_name':
      return member.name || `${PRIVACY_CONFIG.unknownMemberLabel} ${member.memberNumber || ''}`.trim();
      
    case 'first_name':
      const firstName = member.name?.split(' ')[0];
      const lastInitial = member.name?.split(' ').slice(-1)[0]?.charAt(0);
      return firstName && lastInitial ? `${firstName} ${lastInitial}.` : getInitials(member.name);
      
    case 'anonymous':
      return `${PRIVACY_CONFIG.unknownMemberLabel} ${member.memberNumber || Math.floor(Math.random() * 1000)}`;
      
    case 'initials':
    default:
      return getInitials(member.name);
  }
};

/**
 * Get avatar content based on privacy settings
 * @param {Object} member - Member object
 * @returns {string|JSX.Element} Avatar content (initials, icon, or name)
 */
const getAvatarContent = (member) => {
  // Special case for account owner - show crown icon
  if (member.isAccountOwner) {
    return <Crown size={20} />;
  }
  
  // Use initials if enabled, otherwise use first letter of display name
  if (PRIVACY_CONFIG.useInitialsInAvatars) {
    return getInitials(member.name).replace(/\./g, '');
  } else {
    const displayName = getMemberDisplayName(member);
    return displayName.charAt(0).toUpperCase();
  }
};

/**
 * Deduplicate members list to show each unique user only once
 * Prioritizes current user and circle owner, then most recent/verified entries
 * @param {Array} members - Array of member objects
 * @returns {Array} Deduplicated array of unique members
 */
const deduplicateMembers = (members) => {
  if (!members || !Array.isArray(members) || members.length === 0) {
    return [];
  }

  console.log('🔄 Deduplicating members. Original count:', members.length);
  console.log('📋 Original members:', members.map(m => ({
    id: m.id,
    name: m.name,
    email: m.email,
    isCurrentUser: m.isCurrentUser,
    isAccountOwner: m.isAccountOwner,
    isVerified: m.isVerified
  })));

  // Create a Map to store unique members
  // Key priority: email > user_id > name (case-insensitive)
  const uniqueMembers = new Map();

  members.forEach((member, index) => {
    // Generate a unique key for this member
    let uniqueKey = null;
    
    // Priority 1: Use email if available (most reliable)
    if (member.email && typeof member.email === 'string' && member.email.trim()) {
      uniqueKey = `email_${member.email.toLowerCase().trim()}`;
    }
    // Priority 2: Use user_id if available
    else if (member.user_id) {
      uniqueKey = `user_id_${member.user_id}`;
    }
    // Priority 3: Use id if available
    else if (member.id) {
      uniqueKey = `id_${member.id}`;
    }
    // Priority 4: Use name (case-insensitive) - least reliable but better than nothing
    else if (member.name && typeof member.name === 'string' && member.name.trim()) {
      uniqueKey = `name_${member.name.toLowerCase().trim()}`;
    }
    // Priority 5: Use index as fallback (shouldn't happen but prevents errors)
    else {
      uniqueKey = `index_${index}`;
      console.warn('Member has no identifiable unique key, using index:', member);
    }

    // Check if we already have this member
    const existingMember = uniqueMembers.get(uniqueKey);

    if (!existingMember) {
      // First time seeing this member, add them
      uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
      console.log(`➕ Added new member: ${uniqueKey} (${member.name || 'No name'})`);
    } else {
      // We've seen this member before, decide which one to keep
      console.log(`🔄 Duplicate member found: ${uniqueKey} (${member.name || 'No name'})`);
      
      // Priority rules for which member to keep:
      // 1. Always keep current user
      if (member.isCurrentUser && !existingMember.isCurrentUser) {
        uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
        console.log(`👤 Replaced with current user version`);
      }
      // 2. Always keep account owner
      else if (member.isAccountOwner && !existingMember.isAccountOwner) {
        uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
        console.log(`👑 Replaced with account owner version`);
      }
      // 3. Prefer verified members
      else if (member.isVerified && !existingMember.isVerified) {
        uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
        console.log(`✅ Replaced with verified version`);
      }
      // 4. Prefer more recent entries (if we have timestamps)
      else if (member.created_at && existingMember.created_at) {
        const memberDate = new Date(member.created_at);
        const existingDate = new Date(existingMember.created_at);
        if (memberDate > existingDate) {
          uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
          console.log(`📅 Replaced with more recent version`);
        } else {
          console.log(`📅 Kept existing (more recent)`);
        }
      }
      // 5. Prefer entries with more complete data
      else {
        const memberScore = getMemberCompletenessScore(member);
        const existingScore = getMemberCompletenessScore(existingMember);
        
        if (memberScore > existingScore) {
          uniqueMembers.set(uniqueKey, { ...member, _uniqueKey: uniqueKey });
          console.log(`📊 Replaced with more complete data (score: ${memberScore} vs ${existingScore})`);
        } else {
          console.log(`📊 Kept existing (more complete data: ${existingScore} vs ${memberScore})`);
        }
      }
    }
  });

  // Convert Map back to array
  const deduplicatedMembers = Array.from(uniqueMembers.values());

  console.log('✅ Deduplication complete. Final count:', deduplicatedMembers.length);
  console.log('📋 Final members:', deduplicatedMembers.map(m => ({
    id: m.id,
    name: m.name,
    email: m.email,
    isCurrentUser: m.isCurrentUser,
    isAccountOwner: m.isAccountOwner,
    isVerified: m.isVerified,
    uniqueKey: m._uniqueKey
  })));

  // Sort the final list: Current user first, then owner, then verified, then others
  return deduplicatedMembers.sort((a, b) => {
    // Current user always first
    if (a.isCurrentUser && !b.isCurrentUser) return -1;
    if (!a.isCurrentUser && b.isCurrentUser) return 1;
    
    // Account owner second (unless they're also current user)
    if (a.isAccountOwner && !b.isAccountOwner) return -1;
    if (!a.isAccountOwner && b.isAccountOwner) return 1;
    
    // Verified members before unverified
    if (a.isVerified && !b.isVerified) return -1;
    if (!a.isVerified && b.isVerified) return 1;
    
    // Finally, sort by name alphabetically
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Calculate a completeness score for a member to help with deduplication decisions
 * Higher score = more complete data
 * @param {Object} member - Member object
 * @returns {number} Completeness score
 */
const getMemberCompletenessScore = (member) => {
  let score = 0;
  
  // Basic info
  if (member.name && member.name.trim()) score += 10;
  if (member.email && member.email.trim()) score += 10;
  if (member.id) score += 5;
  if (member.user_id) score += 5;
  
  // Status flags
  if (member.isCurrentUser) score += 20;
  if (member.isAccountOwner) score += 15;
  if (member.isVerified) score += 10;
  
  // Additional data
  if (member.phone) score += 5;
  if (member.created_at) score += 3;
  if (member.lastActive) score += 3;
  
  return score;
};

/**
 * CircleTabDetailView Component - Complete Updated Version with Enhanced Circle Data Management
 * 
 * Features:
 * - Complete dynamic data loading from backend APIs
 * - Real-time add-on management with backend persistence
 * - Subscription status awareness (handles canceling subscriptions)
 * - Enhanced error handling and user feedback
 * - Full feature toggle support for plan features and add-ons
 * - Circle member management with verification status
 * - Chat functionality framework
 * - Support ticket system
 * - Responsive design with mobile support
 * - Enhanced accessibility features
 * - Diagnostic capabilities for troubleshooting
 * - ENHANCED: Add-on operations use specific order identifiers from circle
 * - NEW: Enhanced circle data management with order information
 * - PRIVACY: Member names shown as initials for security
 */
const CircleTabDetailView = ({
  circle = {
    id: '123',
    name: 'GreenWelcome',
    plan: 'Unlimited Welcome',
    carrier: 'verizon',
    total_joiners: 2,
    family_size: 4,
  },
  circleDetail = null,
  pricingData = null,
  isLoading = false,
  error = null,
  members = [],
  membersLoading = false,
  membersError = null,
  onBackToCircles,
  onShareCircle,
  onMembersUpdate, // Optional callback for when members are fetched from API
  getCarrierColor,
  getStatusColor,
  darkMode = false
}) => {
  // ==================== STATE MANAGEMENT ====================

  // Management center tab state
  const [activeTab, setActiveTab] = useState(1); // Start with Add-ons tab (index 1)
  const [previousTab, setPreviousTab] = useState(0);

  // Chat functionality state
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatTyping, setChatTyping] = useState(false);

  // Support dialog state
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [supportTopic, setSupportTopic] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  // Feature dialog state
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureDialogLoading, setFeatureDialogLoading] = useState(false);

  // Interactive UI state
  const [hoverFeature, setHoverFeature] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isAutoModeActive, setIsAutoModeActive] = useState(false);
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [showHapticFeedback, setShowHapticFeedback] = useState(false);

  // Dynamic Circle Management Center state - NO SAMPLE DATA
  const [managementLoading, setManagementLoading] = useState(true);
  const [managementError, setManagementError] = useState(null);
  const [circleManagementData, setCircleManagementData] = useState(null);
  const [planFeatures, setPlanFeatures] = useState([]);
  const [planOffers, setPlanOffers] = useState([]);
  const [featureToggling, setFeatureToggling] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Dynamic members state
  const [dynamicMembers, setDynamicMembers] = useState([]);
  const [membersLoadingState, setMembersLoadingState] = useState(false);
  const [membersErrorState, setMembersErrorState] = useState(null);

  // Advanced UI state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [compactView, setCompactView] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);

  // Performance and tracking state
  const [loadTime, setLoadTime] = useState(0);
  const [interactionCount, setInteractionCount] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Subscription status state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // ✅ NEW: Enhanced circle data state for add-on fix
  const [enhancedCircleData, setEnhancedCircleData] = useState(null);

  // Auth0 context
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();

  // Track scroll for parallax effect
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const contentRef = useRef(null);
  const chatEndRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  // ==================== UTILITY FUNCTIONS ====================

  // Helper function to get Auth0 token with proper error handling
  const getToken = async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      return await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });
    } catch (error) {
      console.error('Error getting Auth0 token:', error);
      return null;
    }
  };

  // Feature icon mapping for dynamic features
  const FEATURE_ICON_MAP = {
    'video': Video,
    'wifi': Wifi,
    'globe': Globe,
    'headphones': Headphones,
    'radio': Radio,
    'zap': Zap,
    'shield': Shield,
    'smartphone': Smartphone,
    'phone': Phone,
    'upload': Upload,
    'download': Download,
    'mic': Mic,
    'star': Star,
    'activity': Activity,
    'layers': Layers,
    // AddOn specific icons
    'tv': Video,
    'music': Headphones,
    'gamecontroller': Activity,
    'cloud': Upload,
    'map': Globe,
    'users': Users,
    'gift': Star,
    'percent': Star,
    'checksquare': Check,
    'watch': Smartphone,
    'default': Settings
  };

  // Get feature icon component with fallback
  const getFeatureIcon = (iconName) => {
    const IconComponent = FEATURE_ICON_MAP[iconName?.toLowerCase()] || FEATURE_ICON_MAP['default'];
    return <IconComponent size={20} />;
  };

  // Utility functions for notifications and feedback
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
    console.log(`NOTIFICATION [${severity.toUpperCase()}]:`, message);
  };

  const showFeedback = (message, type = 'success') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setIsFeedbackVisible(true);
    setTimeout(() => setIsFeedbackVisible(false), 3000);

    // Haptic feedback simulation
    if (showHapticFeedback && 'vibrate' in navigator) {
      navigator.vibrate(type === 'success' ? [50] : [100, 50, 100]);
    }
  };

  // Track user interactions for analytics
  const trackInteraction = (action, details = {}) => {
    setInteractionCount(prev => prev + 1);
    setLastActivity(Date.now());
    console.log('USER_INTERACTION:', { action, details, timestamp: Date.now() });
  };

  // ==================== CORE FUNCTIONS ====================

  // Track mouse movement for ambient lighting effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Track scroll for parallax effects
  useEffect(() => {
    let lastScrollY = 0;

    const handleScroll = () => {
      if (contentRef.current) {
        const currentScrollY = contentRef.current.scrollTop;
        setScrollY(currentScrollY);
        setScrollDirection(currentScrollY > lastScrollY ? 'down' : 'up');
        lastScrollY = currentScrollY;
      }
    };

    const currentRef = contentRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [contentRef]);

  // Initialize component and track load time
  useEffect(() => {
    const loadStartTime = startTimeRef.current;
    const loadEndTime = Date.now();
    setLoadTime(loadEndTime - loadStartTime);

    console.log(`CircleTabDetailView loaded in ${loadEndTime - loadStartTime}ms`);
  }, []);

  // ✅ ENHANCED: Fetch circle data on mount with enhanced order information
  useEffect(() => {
    if (circle?.id) {
      debugCircleDataSources();
      fetchCircleManagementData();
      fetchEnhancedCircleData(); // ✅ NEW: Fetch enhanced circle data
      fetchSubscriptionStatus();
      // Also fetch members if not provided via props
      if (!members || members.length === 0) {
        fetchCircleMembers();
      } else {
        setDynamicMembers(members);
      }
    }
  }, [circle?.id]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current && activeTab === 2) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  // Sync members from props if they change
  useEffect(() => {
    if (members && members.length > 0) {
      // ✅ NEW: Apply deduplication to members from props as well
      const deduplicatedMembers = deduplicateMembers(members);
      setDynamicMembers(deduplicatedMembers);
    }
  }, [members]);

  // ✅ NEW: Enhanced function to fetch and merge circle data with user order info
  // ✅ ENHANCED: Fetch circle data with comprehensive debugging
  const fetchEnhancedCircleData = async () => {
    try {
      const token = await getToken();
      if (!token || !circle?.id) {
        console.log('❌ No token or circle ID available for fetchEnhancedCircleData');
        return;
      }

      console.log(`🔍 Fetching enhanced circle data for circle ${circle.id} (${circle.name})`);
      console.log(`📋 Original circle data:`, {
        id: circle.id,
        name: circle.name,
        isOwner: circle.isOwner,
        source: circle.source,
        status: circle.status,
        statusDisplay: circle.statusDisplay,
        subscription_status: circle.subscription_status,
        stripe_subscription_id: circle.stripe_subscription_id,
        order_number: circle.order_number
      });

      // Check if we already have the data we need from the main API
      if (circle.stripe_subscription_id && circle.order_number) {
        console.log(`✅ Circle already has required identifiers from main API:`, {
          stripe_subscription_id: circle.stripe_subscription_id,
          order_number: circle.order_number
        });

        const enhancedCircle = { ...circle };
        setEnhancedCircleData(enhancedCircle);
        return enhancedCircle;
      }

      // Try to fetch user's order information for this circle
      console.log(`📡 Calling getUserOrderForCircle API for circle ${circle.id}...`);

      try {
        const userOrderResponse = await circleService.getUserOrderForCircle(circle.id, token);

        console.log('📋 getUserOrderForCircle API response:', userOrderResponse);

        if (userOrderResponse?.data) {
          const orderData = userOrderResponse.data;
          console.log('✅ Successfully retrieved order data:', {
            id: orderData.id,
            order_number: orderData.order_number,
            stripe_subscription_id: orderData.stripe_subscription_id,
            status: orderData.status,
            subscription_status: orderData.subscription_status,
            circle_id: orderData.circle_id,
            circle_name: orderData.circle_name
          });

          // Create enhanced circle object with order information
          const enhancedCircle = {
            ...circle,
            // Add order information to circle object
            stripe_subscription_id: orderData.stripe_subscription_id || null,
            order_number: orderData.order_number || null,
            subscription_status: orderData.subscription_status || circle.subscription_status,
            user_order_id: orderData.id || null,
            // Add additional order details for debugging
            order_status: orderData.status || null,
            shipping_method: orderData.shipping_method || circle.shipping_method,
            joining_condition: orderData.joining_condition || circle.joining_condition
          };

          console.log('✅ Enhanced circle data created:', {
            id: enhancedCircle.id,
            name: enhancedCircle.name,
            stripe_subscription_id: enhancedCircle.stripe_subscription_id,
            order_number: enhancedCircle.order_number,
            subscription_status: enhancedCircle.subscription_status,
            order_status: enhancedCircle.order_status
          });

          setEnhancedCircleData(enhancedCircle);
          return enhancedCircle;

        } else {
          console.log('⚠️ getUserOrderForCircle returned no data');
        }

      } catch (apiError) {
        console.log('❌ getUserOrderForCircle API call failed:', {
          error: apiError,
          status: apiError?.response?.status,
          statusText: apiError?.response?.statusText,
          data: apiError?.response?.data
        });

        // Log the specific error details
        if (apiError?.response?.data) {
          console.log('📋 API Error Details:', apiError.response.data);
        }

        // Check if it's a 404 (no order found)
        if (apiError?.response?.status === 404) {
          console.log('🔍 No order found for this user/circle combination');
          console.log('🔍 This might be because:');
          console.log('   - User doesn\'t have an active order for this circle');
          console.log('   - Order status doesn\'t match the API filter criteria');
          console.log('   - User is circle owner but hasn\'t created an order yet');
        }
      }

      // If we get here, we couldn't get the order data
      // But let's still check if the original circle has any useful data
      console.log('ℹ️ Could not fetch enhanced order data, using original circle data');

      const basicCircle = {
        ...circle,
        // Ensure these fields exist even if null
        stripe_subscription_id: circle.stripe_subscription_id || null,
        order_number: circle.order_number || null,
        subscription_status: circle.subscription_status || null
      };

      console.log('📋 Using basic circle data:', {
        id: basicCircle.id,
        name: basicCircle.name,
        stripe_subscription_id: basicCircle.stripe_subscription_id,
        order_number: basicCircle.order_number,
        subscription_status: basicCircle.subscription_status
      });

      setEnhancedCircleData(basicCircle);
      return basicCircle;

    } catch (error) {
      console.error('❌ Error in fetchEnhancedCircleData:', error);

      // Fallback to basic circle data
      const fallbackCircle = {
        ...circle,
        stripe_subscription_id: circle.stripe_subscription_id || null,
        order_number: circle.order_number || null,
        subscription_status: circle.subscription_status || null
      };

      setEnhancedCircleData(fallbackCircle);
      return fallbackCircle;
    }
  };

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      setSubscriptionLoading(true);
      const token = await getToken();
      if (!token || !circle?.id) return;

      console.log(`Fetching subscription status for circle: ${circle.id}`);

      // Try to get user's order for this circle
      const response = await circleService.getUserOrderForCircle(circle.id, token);

      if (response && response.data) {
        const orderData = response.data;
        const status = orderData.status?.toLowerCase();
        setSubscriptionStatus(status);
        setSubscriptionInfo(orderData);

        console.log('SUBSCRIPTION STATUS:', orderData.status);

        // If subscription is canceling, show a warning
        if (status === 'canceling') {
          showNotification(
            'Your subscription is currently being cancelled. Add-ons cannot be added until you reactivate.',
            'warning'
          );
        }
      }
    } catch (error) {
      console.log('Could not fetch subscription status:', error);
      // Don't show error for this as it's not critical
      // Set to null so we don't block functionality
      setSubscriptionStatus(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const debugCircleDataSources = () => {
    console.log('🔍 === CIRCLE DATA SOURCE DEBUGGING ===');

    // Log all available circle data
    console.log('📋 Circle prop data:', {
      id: circle?.id,
      name: circle?.name,
      source: circle?.source,
      isOwner: circle?.isOwner,
      status: circle?.status,
      statusDisplay: circle?.statusDisplay,
      subscription_status: circle?.subscription_status,
      stripe_subscription_id: circle?.stripe_subscription_id,
      order_number: circle?.order_number,
      user_order_id: circle?.user_order_id,
      // Check for any other potential order-related fields
      orderId: circle?.orderId,
      orderNumber: circle?.orderNumber,
      stripeSubscriptionId: circle?.stripeSubscriptionId,
      subscriptionId: circle?.subscriptionId
    });

    console.log('📋 CircleDetail prop data:', {
      circle: circleDetail?.circle,
      selected_plan: circleDetail?.selected_plan,
      // Check if order data is in circleDetail
      order: circleDetail?.order,
      subscription: circleDetail?.subscription,
      order_info: circleDetail?.order_info
    });

    // Check if the data might be nested differently
    console.log('📋 Checking for nested order data in circle:', {
      circle_order: circle?.order,
      circle_subscription: circle?.subscription,
      circle_payment: circle?.payment,
      circle_billing: circle?.billing
    });

    // Log the enhanced circle data state
    console.log('📋 Enhanced circle data state:', enhancedCircleData);

    console.log('🔍 === END CIRCLE DATA DEBUGGING ===');
  };

  // ENHANCED: Fetch circle management data with comprehensive AddOn integration - NO SAMPLE DATA
  const fetchCircleManagementData = async () => {
    try {
      setManagementLoading(true);
      setManagementError(null);
      trackInteraction('fetch_circle_management_data', { circleId: circle.id });

      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required to load circle data');
      }

      console.log(`DYNAMIC: Fetching circle management data for circle ${circle.id}`);

      // Fetch circle management data, available add-ons, and user's current add-ons
      const [managementResponse, availableAddOnsResponse, userAddOnsResponse] = await Promise.all([
        circleService.getCircleManagementData(circle.id, token),
        circleService.getCircleAvailableAddOns(circle.id, token),
        circleService.getUserCircleAddOns(circle.id, token)
      ]);

      console.log('DYNAMIC: Circle management data received:', managementResponse);
      console.log('DYNAMIC: Available add-ons received:', availableAddOnsResponse);
      console.log('DYNAMIC: User add-ons received:', userAddOnsResponse);

      // Handle different response structures
      const managementData = managementResponse.data || managementResponse;
      const availableAddOnsData = availableAddOnsResponse.data || availableAddOnsResponse;
      const userAddOnsData = userAddOnsResponse.data || userAddOnsResponse;

      setCircleManagementData(managementData);

      // Process plan features (included with plan)
      let processedFeatures = [];

      // Try to get features from various possible locations in the response
      const featuresSource = managementData.selected_plan?.plan_features?.plan_features ||
        managementData.plan_features ||
        managementData.features ||
        [];

      if (featuresSource.length > 0) {
        processedFeatures = featuresSource.map(feature => ({
          id: feature.id || `feature_${Date.now()}_${Math.random()}`,
          name: feature.name || 'Unknown Feature',
          icon: getFeatureIcon(feature.icon),
          enabled: feature.is_enabled !== undefined ? feature.is_enabled : false,
          toggleable: feature.is_toggleable !== undefined ? feature.is_toggleable : true,
          description: feature.short_description || feature.description || 'No description available',
          fullDescription: feature.full_description || feature.description || 'No detailed description available',
          cost: feature.monthly_cost || 0,
          category: feature.category || 'general',
          featureType: feature.feature_type || 'feature',
          priority: feature.priority || 'medium',
          usage: feature.usage_info || null
        }));
      }

      // Process available add-ons and match with user's active add-ons
      const availableAddOns = availableAddOnsData.data || availableAddOnsData || [];
      const userActiveAddOns = userAddOnsData.data || userAddOnsData || [];

      // Create a map of user's active add-ons for quick lookup
      const userAddOnMap = {};
      userActiveAddOns.forEach(userAddOn => {
        // Handle different possible ID fields
        const addonId = userAddOn.addon_id || userAddOn.id;
        if (addonId) {
          userAddOnMap[addonId] = userAddOn;
        }
      });

      let processedOffers = [];

      if (availableAddOns.length > 0) {
        processedOffers = availableAddOns.map(addon => {
          const userAddOn = userAddOnMap[addon.id];

          return {
            id: addon.id,
            name: addon.name || 'Unknown Add-on',
            icon: getFeatureIcon(addon.icon),
            enabled: !!userAddOn, // Check if user has this add-on
            toggleable: true, // Add-ons are always toggleable
            description: addon.short_description || addon.description || 'No description available',
            fullDescription: addon.full_description || addon.description || 'No detailed description available',
            cost: `$${addon.monthly_cost || 0}/mo`,
            category: addon.addon_type || 'other',
            featureType: 'addon',
            priority: addon.priority || 'medium',
            savings: addon.savings_info || null,
            isRecommended: addon.is_recommended || false,
            isPopular: addon.is_popular || false,
            hasTrial: addon.has_trial || false,
            trialDays: addon.trial_days || 0,
            requiresVerification: addon.requires_verification || false,
            userAddOnId: userAddOn?.user_addon_id || userAddOn?.id || null,
            isInTrial: userAddOn?.is_trial || false,
            displayOrder: addon.display_order || 0
          };
        }).sort((a, b) => {
          // Sort by display order, then by priority, then by name
          if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder;
          }
          const priorityOrder = { high: 1, medium: 2, low: 3 };
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          }
          return a.name.localeCompare(b.name);
        });
      }

      setPlanFeatures(processedFeatures);
      setPlanOffers(processedOffers);

      if (processedFeatures.length === 0 && processedOffers.length === 0) {
        showNotification('No features or add-ons available for this circle', 'info');
      } else {
        showNotification(`Loaded ${processedFeatures.length} features and ${processedOffers.length} add-ons`, 'success');
      }

    } catch (err) {
      console.error('DYNAMIC: Error fetching circle management data:', err);
      const errorMessage = err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load circle data';
      setManagementError(errorMessage);

      // Don't use fallback data - show error instead
      setPlanFeatures([]);
      setPlanOffers([]);

      showNotification(`Failed to load circle data: ${errorMessage}`, 'error');
    } finally {
      setManagementLoading(false);
    }
  };

  // Fetch circle members from API
  const fetchCircleMembers = async () => {
    try {
      setMembersLoadingState(true);
      setMembersErrorState(null);

      const token = await getToken();
      if (!token) {
        console.log('No token available for fetching members');
        return;
      }

      console.log(`Fetching members for circle: ${circle.id}`);
      const response = await circleService.getCircleMembers(circle.id, token);

      const membersData = response.data || response;
      console.log('Circle members received (before deduplication):', membersData);

      // ✅ NEW: Apply deduplication to remove duplicate members
      const deduplicatedMembers = deduplicateMembers(membersData);
      console.log('Circle members after deduplication:', deduplicatedMembers);

      setDynamicMembers(deduplicatedMembers);

      // If we have a callback to update parent component
      if (typeof onMembersUpdate === 'function') {
        onMembersUpdate(deduplicatedMembers);
      }
    } catch (error) {
      console.error('Error fetching circle members:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load members';
      setMembersErrorState(errorMessage);
      // Don't show error notification for members as it's not critical for main functionality
    } finally {
      setMembersLoadingState(false);
    }
  };

  // ✅ ENHANCED: Refresh all data including enhanced circle data
  const refreshAllData = async () => {
    setManagementLoading(true);
    try {
      await Promise.all([
        fetchCircleManagementData(),
        fetchCircleMembers(),
        fetchSubscriptionStatus(),
        fetchEnhancedCircleData() // ✅ NEW: Refresh enhanced circle data
      ]);
      showNotification('Data refreshed successfully', 'success');
    } catch (error) {
      console.error('Error refreshing data:', error);
      showNotification('Failed to refresh some data', 'warning');
    }
  };

  // Auto-refresh data every 5 minutes if user is active
  useEffect(() => {
    const interval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      // Only refresh if user was active in the last 2 minutes
      if (timeSinceLastActivity < 120000) {
        console.log('Auto-refreshing circle data...');
        refreshAllData();
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [lastActivity]);

  // Handle retry for failed API calls
  const handleRetry = () => {
    setManagementError(null);
    fetchCircleManagementData();
  };

  // Diagnostic helper function
  const callDiagnosticEndpoint = async (circleId) => {
    try {
      const token = await getToken();
      if (!token) return;

      console.log(`Calling diagnostic endpoint for circle ${circleId}`);

      const response = await apiClient.get(
        `/api/circles/${circleId}/diagnose-order/`,
        withAuth(token)
      );

      console.log('DIAGNOSTIC RESPONSE:', response.data);

      // Show diagnostic information in a more user-friendly way
      if (response.data?.diagnostic_info) {
        const diagnostic = response.data.diagnostic_info;

        if (diagnostic.order_model?.missing_fields?.length > 0) {
          console.log('Missing required fields for order creation:',
            diagnostic.order_model.missing_fields.map(f => f.name).join(', '));
        }

        if (!diagnostic.existing_order?.exists && diagnostic.user?.is_circle_owner) {
          console.log('Circle owner needs order creation with fields:',
            diagnostic.proposed_order_data);
        }
      }
    } catch (diagError) {
      console.error('Diagnostic call failed:', diagError);
    }
  };

  // ✅ ENHANCED: Handle feature toggle with enhanced circle data for add-on fix
  // ✅ ENHANCED: Handle feature toggle with enhanced circle data for add-on fix
  const handleFeatureToggle = async (featureId, enabled) => {
    // ✅ FIX: Move circleDataToUse declaration outside try-catch so it's accessible in both blocks
    const circleDataToUse = enhancedCircleData || circle;

    try {
      setFeatureToggling(prev => ({ ...prev, [featureId]: true }));
      trackInteraction('feature_toggle', { featureId, enabled });

      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required - please log in to manage features');
      }

      // Find the feature being toggled - check both features and add-ons
      const feature = [...planFeatures, ...planOffers].find(f => f.id === featureId);

      if (!feature) {
        throw new Error(`Feature with ID ${featureId} not found`);
      }

      console.log(`DYNAMIC: Toggling ${feature?.featureType || 'feature'} ${feature?.name} (ID: ${featureId}) to ${enabled}`);

      console.log(`🔍 Enhanced Circle data for add-on operation:`, {
        id: circleDataToUse.id,
        name: circleDataToUse.name,
        stripe_subscription_id: circleDataToUse.stripe_subscription_id,
        order_number: circleDataToUse.order_number,
        subscription_status: circleDataToUse.subscription_status
      });

      let response;

      // Handle differently based on feature type
      if (feature?.featureType === 'addon') {
        // ✅ ENHANCED: Pass enhancedCircleData with order identifiers
        response = await circleService.toggleCircleAddon(
          circleDataToUse.id,
          featureId,
          enabled,
          token,
          circleDataToUse  // 🎯 Pass enhanced circle object with order identifiers
        );

        console.log('DYNAMIC: Add-on toggle response:', response);

        // Update add-ons state with more comprehensive response handling
        setPlanOffers(prev =>
          prev.map(f => {
            if (f.id === featureId) {
              const responseData = response.data || response;
              return {
                ...f,
                enabled,
                // Update with any additional data from response
                userAddOnId: enabled ? (responseData?.addon?.user_addon_id || responseData?.user_addon_id) : null,
                isInTrial: enabled ? (responseData?.addon?.is_trial || responseData?.is_trial || false) : false
              };
            }
            return f;
          })
        );

        // Show success message with additional context
        const successMessage = enabled
          ? `${feature.name} added successfully${feature.hasTrial ? ' (trial started)' : ''}`
          : `${feature.name} removed successfully`;
        showNotification(successMessage, 'success');

      } else {
        // This is a plan feature, use feature API
        response = await circleService.toggleCircleFeature(
          circleDataToUse.id,
          featureId,
          enabled,
          token
        );

        console.log('DYNAMIC: Feature toggle response:', response);

        // Update features state
        setPlanFeatures(prev =>
          prev.map(f => {
            if (f.id === featureId) {
              const responseData = response.data || response;
              return {
                ...f,
                enabled,
                // Update with any additional data from response
                usage: responseData?.feature?.usage_info || f.usage
              };
            }
            return f;
          })
        );

        // Show success message
        const successMessage = `${feature.name} ${enabled ? 'enabled' : 'disabled'} successfully`;
        showNotification(successMessage, 'success');
      }

      // Show UI feedback
      showFeedback(`${enabled ? 'Enabled' : 'Disabled'} ${feature?.name}`, enabled ? 'success' : 'info');

      // Special handling for specific features that might require additional actions
      if (feature?.name?.toLowerCase().includes('hd streaming') && enabled) {
        // Send HD streaming notification if available
        try {
          await circleService.sendHDStreamingNotification({
            circle_id: circleDataToUse.id,
            feature_name: feature.name,
            enabled: enabled
          }, token);
        } catch (notificationError) {
          console.warn('HD streaming notification failed:', notificationError);
          // Don't fail the main operation if notification fails
        }
      }

    } catch (err) {
      console.error('DYNAMIC: Error toggling feature:', err);

      // Rollback optimistic update on error
      const feature = [...planFeatures, ...planOffers].find(f => f.id === featureId);

      if (feature?.featureType === 'addon') {
        setPlanOffers(prev =>
          prev.map(f => f.id === featureId ? { ...f, enabled: !enabled } : f)
        );
      } else {
        setPlanFeatures(prev =>
          prev.map(f => f.id === featureId ? { ...f, enabled: !enabled } : f)
        );
      }

      // Enhanced error message handling with subscription status awareness
      let errorMessage = 'Failed to update feature. Please try again.';
      let errorSeverity = 'error';

      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;

        // Special handling for canceling subscription error
        if (errorMessage.toLowerCase().includes('canceling subscription') ||
          errorMessage.toLowerCase().includes('cannot add add-ons to a canceling')) {
          errorMessage = 'Cannot add add-ons to a canceling subscription. Please reactivate your subscription first or contact support.';
          errorSeverity = 'warning';

          // Show subscription status info if available
          if (err.response.data.subscription_info) {
            const subInfo = err.response.data.subscription_info;
            console.log('SUBSCRIPTION INFO:', subInfo);

            // Show additional context in a separate notification
            setTimeout(() => {
              showNotification(
                `Your subscription (${subInfo.order_number}) is currently ${subInfo.status}. Reactivate to add add-ons.`,
                'info'
              );
            }, 2000);
          }
        }

        // Check if we have diagnostic information
        if (err.response.data.debug_info) {
          console.log('DIAGNOSTIC INFO:', err.response.data.debug_info);
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Special handling for common errors
      if (errorMessage.toLowerCase().includes('authentication')) {
        errorMessage = 'Authentication required. Please log in and try again.';
      } else if (errorMessage.toLowerCase().includes('permission')) {
        errorMessage = 'You don\'t have permission to modify this feature.';
      } else if (errorMessage.toLowerCase().includes('verification')) {
        errorMessage = 'This feature requires carrier verification before activation.';
      } else if (errorMessage.toLowerCase().includes('subscription for circle owner')) {
        errorMessage = 'Unable to create subscription. Please contact support for assistance.';
        // ✅ FIX: Now circleDataToUse is accessible here since it's declared outside try-catch
        if (feature?.featureType === 'addon') {
          callDiagnosticEndpoint(circleDataToUse.id);
        }
      }

      showNotification(errorMessage, errorSeverity);
      showFeedback('Feature update failed', 'error');
    } finally {
      setFeatureToggling(prev => ({ ...prev, [featureId]: false }));
    }
  };

  // Enhanced chat functionality
  const handleChatSend = () => {
    if (chatMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        sender: user?.name || 'You',
        message: chatMessage.trim(),
        timestamp: new Date(),
        isCurrentUser: true,
        avatar: user?.name?.charAt(0) || 'Y'
      };

      setChatMessages(prev => [...prev, newMessage]);
      setChatMessage('');
      trackInteraction('chat_send', { messageLength: chatMessage.length });

      // Simulate typing indicator for demo
      setChatTyping(true);
      setTimeout(() => setChatTyping(false), 2000);

      showFeedback('Message sent', 'success');
    }
  };

  // Enhanced support functionality
  const handleSupportSubmit = async () => {
    if (supportTopic && supportMessage) {
      setSupportSubmitting(true);
      trackInteraction('support_submit', { topic: supportTopic });

      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        console.log('Support request:', {
          topic: supportTopic,
          message: supportMessage,
          circle: circle.id,
          timestamp: new Date()
        });

        setShowSupportDialog(false);
        setSupportTopic('');
        setSupportMessage('');

        showNotification('Support request submitted successfully! We\'ll get back to you within 24 hours.', 'success');
        showFeedback('Support ticket created', 'success');
      } catch (error) {
        showNotification('Failed to submit support request. Please try again.', 'error');
        showFeedback('Failed to create ticket', 'error');
      } finally {
        setSupportSubmitting(false);
      }
    }
  };

  // Enhanced feature dialog functionality
  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setShowFeatureDialog(true);
    trackInteraction('feature_view', { featureId: feature.id, featureName: feature.name });
  };

  // Tab change handler with transition effects
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      setPreviousTab(activeTab);
      setActiveTab(newTab);
      trackInteraction('tab_change', { from: activeTab, to: newTab });
    }
  };

  // ✅ ENHANCED: Debug logging for component updates including enhanced circle data
  useEffect(() => {
    console.log("CircleTabDetailView received circleDetail:", circleDetail);
    console.log("CircleTabDetailView received members:", members);
    console.log("CircleTabDetailView dark mode:", darkMode);
    console.log("DYNAMIC: Current planFeatures:", planFeatures);
    console.log("DYNAMIC: Current planOffers:", planOffers);
    console.log("✅ ENHANCED: Enhanced circle data:", enhancedCircleData); // ✅ NEW
  }, [circleDetail, members, darkMode, planFeatures, planOffers, enhancedCircleData]);

  // ==================== SUBSCRIPTION STATUS FUNCTIONS ====================

  // Check if user can modify add-ons based on subscription status
  const canModifyAddons = !subscriptionStatus ||
    ['active', 'trialing', 'paid', 'port in progress', 'portinprogress'].includes(subscriptionStatus);

  const isSubscriptionCanceling = subscriptionStatus === 'canceling';
  const isSubscriptionInactive = subscriptionStatus &&
    ['canceling', 'cancelled', 'canceled', 'inactive', 'expired'].includes(subscriptionStatus);

  // ==================== THEME CONFIGURATION ====================

  // Define theme-based colors - simplified for clean design
  const colors = {
    background: darkMode ? '#1A1B25' : 'white',
    border: darkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.8)',
    text: {
      primary: darkMode ? '#F9FAFB' : '#1A1B25',
      secondary: darkMode ? '#D1D5DB' : '#767991',
      muted: darkMode ? '#9CA3AF' : '#9CA3AF'
    },
    primary: {
      main: darkMode ? '#818cf8' : '#6366F1',
    },
    secondary: {
      main: darkMode ? '#7dd3fc' : '#0EA5E9',
    },
    success: {
      main: darkMode ? '#4ade80' : '#10B981',
      background: darkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)'
    },
    warning: {
      main: darkMode ? '#fbbf24' : '#F59E0B',
      background: darkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
    },
    error: {
      main: darkMode ? '#f87171' : '#EF4444',
      background: darkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)'
    },
    info: {
      main: darkMode ? '#3B82F6' : '#3B82F6',
      background: darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'
    },
    paper: {
      bg: darkMode ? '#1E293B' : 'white',
      border: darkMode ? 'rgba(75, 85, 99, 0.3)' : 'transparent',
    },
    accent: {
      pink: {
        main: '#FF4775',
        light: '#FF7799',
        dark: '#E31B5F',
      }
    }
  };

  // Get member status for chat tab
  const getMemberStatus = (member) => {
    if (member.lastActive) {
      const lastActive = new Date(member.lastActive);
      const now = new Date();
      const diffMinutes = Math.floor((now - lastActive) / (1000 * 60));

      if (diffMinutes < 5) return { status: 'online', text: 'Online' };
      if (diffMinutes < 60) return { status: 'away', text: `${diffMinutes}m ago` };
      if (diffMinutes < 1440) return { status: 'offline', text: `${Math.floor(diffMinutes / 60)}h ago` };
      return { status: 'offline', text: `${Math.floor(diffMinutes / 1440)}d ago` };
    }
    return { status: 'unknown', text: 'Unknown' };
  };

  // Status indicator colors for chat
  const getChatStatusColor = (status) => {
    switch (status) {
      case 'online': return '#10B981';
      case 'away': return '#F59E0B';
      case 'offline': return '#9CA3AF';
      default: return '#9CA3AF';
    }
  };

  // Get circle name and plan from API or props
  const circleName = circleDetail?.circle?.name || circle.name;
  const circlePlan = circleDetail?.selected_plan?.name || circle.plan;
  const circleStatus = circleDetail?.circle?.status || circle.statusDisplay;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`circle-detail-view ${darkMode ? 'dark-mode' : ''}`}
    >
      {/* Header with back button */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => {
              onBackToCircles();
              trackInteraction('back_to_circles');
            }}
            sx={{
              mr: 2,
              p: 1.5,
              background: alpha(colors.accent.pink.main, 0.1),
              borderRadius: '12px',
              '&:hover': {
                background: alpha(colors.accent.pink.main, 0.2)
              }
            }}
          >
            <ArrowLeft size={20} color={colors.accent.pink.main} />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontSize: '0.9375rem',
              fontWeight: 500
            }}
          >
            {circleName} - {circlePlan}
          </Typography>
        </Box>

        {/* Status chip and settings */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={circleStatus || "Unverified"}
            size="small"
            sx={{
              bgcolor: alpha(colors.secondary.main, 0.1),
              color: colors.secondary.main,
              fontWeight: 500,
              fontSize: '0.75rem',
              borderRadius: '12px',
              height: 28,
            }}
          />

          {/* Advanced options toggle */}
          <Tooltip title="Settings">
            <IconButton
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              sx={{
                color: colors.text.secondary,
                '&:hover': { color: colors.accent.pink.main }
              }}
            >
              <Settings size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Advanced options panel */}
      <Collapse in={showAdvancedOptions}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            background: colors.paper.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Animation Speed:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0.5, 1, 1.5, 2].map((speed) => (
                  <Button
                    key={speed}
                    size="small"
                    variant={animationSpeed === speed ? 'contained' : 'outlined'}
                    onClick={() => setAnimationSpeed(speed)}
                    sx={{
                      minWidth: 40,
                      py: 0.5,
                      ...(animationSpeed === speed && {
                        bgcolor: colors.accent.pink.main,
                        '&:hover': { bgcolor: colors.accent.pink.dark }
                      })
                    }}
                  >
                    {speed}x
                  </Button>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Compact:
              </Typography>
              <Switch
                size="small"
                checked={compactView}
                onChange={(e) => setCompactView(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.accent.pink.main,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.accent.pink.main,
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Metrics:
              </Typography>
              <Switch
                size="small"
                checked={showMetrics}
                onChange={(e) => setShowMetrics(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.accent.pink.main,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.accent.pink.main,
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* Performance metrics overlay */}
      {showMetrics && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            top: 10,
            right: 10,
            background: colors.paper.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            p: 1.5,
            zIndex: 1000,
            fontSize: '0.75rem',
            color: colors.text.secondary
          }}
        >
          <Typography variant="caption" display="block">Load: {loadTime}ms</Typography>
          <Typography variant="caption" display="block">Interactions: {interactionCount}</Typography>
          <Typography variant="caption" display="block">Last: {Math.round((Date.now() - lastActivity) / 1000)}s ago</Typography>
          {subscriptionStatus && (
            <Typography variant="caption" display="block">Status: {subscriptionStatus}</Typography>
          )}
          {enhancedCircleData?.stripe_subscription_id && (
            <Typography variant="caption" display="block">Sub ID: {enhancedCircleData.stripe_subscription_id.slice(-6)}</Typography>
          )}
        </Paper>
      )}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3
      }}>
        {/* Left Column - Circle Management Center */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          background: colors.paper.bg,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${colors.paper.border}`,
          height: '100%',
        }}>
          {/* Header with Title */}
          <Box sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: colors.border
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{
                display: 'flex',
                color: colors.accent.pink.main,
                mr: 1.5
              }}>
                <Settings size={18} />
              </Box>
              <Typography sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: colors.text.primary
              }}>
                Circle Management Center
              </Typography>
              {managementLoading && (
                <CircularProgress size={16} sx={{ color: colors.accent.pink.main, ml: 2 }} />
              )}
            </Box>

            <Typography sx={{
              ml: 7,
              fontSize: '0.9375rem',
              color: colors.text.secondary
            }}>
              Manage your circle features, add-ons, chat with members, and get support
            </Typography>
          </Box>

          {/* Tab Navigation */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: colors.border,
          }}>
            {[
              { icon: <Settings size={18} />, label: 'Plan Features', count: planFeatures.filter(f => f.enabled).length },
              { icon: <PlusCircle size={18} />, label: 'Add-ons', count: planOffers.filter(f => f.enabled).length },
              { icon: <MessageCircle size={18} />, label: 'Circle Chat', count: chatMessages.length },
              { icon: <HelpCircle size={18} />, label: 'Support' }
            ].map((tab, index) => (
              <Box
                key={index}
                onClick={() => handleTabChange(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  cursor: 'pointer',
                  opacity: activeTab === index ? 1 : 0.7,
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
              >
                <Box sx={{
                  color: activeTab === index
                    ? colors.accent.pink.main
                    : colors.text.secondary,
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {tab.icon}
                  {tab.count !== undefined && tab.count > 0 && (
                    <Chip
                      label={tab.count}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.625rem',
                        bgcolor: activeTab === index ? colors.accent.pink.main : alpha(colors.text.secondary, 0.1),
                        color: activeTab === index ? 'white' : colors.text.secondary,
                        minWidth: 16
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: activeTab === index ? 600 : 500,
                    color: activeTab === index
                      ? colors.accent.pink.main
                      : colors.text.secondary,
                  }}
                >
                  {compactView ? '' : tab.label}
                </Typography>
                {activeTab === index && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      right: '10%',
                      height: 2,
                      backgroundColor: colors.accent.pink.main,
                      borderRadius: 4
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Main content area */}
          <Box sx={{
            flexGrow: 1,
            p: 0,
            overflowY: 'auto',
            maxHeight: compactView ? '40vh' : '500px'
          }}>
            {managementLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                <CircularProgress size={48} sx={{ color: colors.accent.pink.main }} />
              </Box>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 / animationSpeed }}
                >
                  {/* Tab 0: Plan Features */}
                  {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>
                      {managementError && (
                        <Alert
                          severity="error"
                          sx={{ mb: 2, borderRadius: 2 }}
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              onClick={handleRetry}
                              startIcon={<RefreshCw size={16} />}
                            >
                              Retry
                            </Button>
                          }
                        >
                          {managementError}
                        </Alert>
                      )}

                      {planFeatures.length === 0 && !managementLoading && (
                        <Box sx={{
                          textAlign: 'center',
                          py: 4
                        }}>
                          <Settings
                            size={48}
                            style={{
                              color: colors.text.muted,
                              marginBottom: '16px',
                              opacity: 0.5
                            }}
                          />
                          <Typography sx={{ color: colors.text.secondary }}>
                            No plan features available
                          </Typography>
                        </Box>
                      )}

                      {planFeatures.map((feature) => {
                        const isToggling = featureToggling[feature.id];
                        return (
                          <Box
                            key={feature.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 2,
                              mb: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: colors.border,
                              background: feature.enabled
                                ? alpha(colors.success.main, 0.05)
                                : 'transparent',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <Box sx={{
                              minWidth: 40,
                              color: feature.enabled ? colors.success.main : colors.text.muted,
                              mr: 2
                            }}>
                              {feature.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{
                                fontWeight: 600,
                                color: colors.text.primary,
                                mb: 0.5
                              }}>
                                {feature.name}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.875rem',
                                color: colors.text.secondary
                              }}>
                                {feature.cost || feature.description}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: colors.text.muted,
                                mt: 0.5
                              }}>
                                {feature.description}
                              </Typography>
                              {feature.usage && (
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  color: colors.info.main,
                                  mt: 0.5
                                }}>
                                  {feature.usage}
                                </Typography>
                              )}
                            </Box>
                            {feature.toggleable && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isToggling && (
                                  <CircularProgress size={16} sx={{ color: colors.accent.pink.main }} />
                                )}
                                <Switch
                                  checked={feature.enabled}
                                  onChange={(e) => handleFeatureToggle(feature.id, e.target.checked)}
                                  disabled={isToggling}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: colors.accent.pink.main,
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: colors.accent.pink.main,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                      })}

                      {planFeatures.length > 0 && (
                        <Alert
                          severity="warning"
                          sx={{
                            mt: 3,
                            borderRadius: 2,
                            backgroundColor: colors.warning.background,
                            border: '1px solid',
                            borderColor: alpha(colors.warning.main, 0.2),
                          }}
                        >
                          Some features may incur additional charges or require carrier approval.
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Tab 1: Add-ons - ENHANCED WITH SUBSCRIPTION STATUS HANDLING */}
                  {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                      {/* Subscription Status Warning for Canceling Subscriptions */}
                      {isSubscriptionCanceling && (
                        <Alert
                          severity="warning"
                          sx={{
                            mb: 3,
                            borderRadius: 2,
                            backgroundColor: colors.warning.background,
                            border: '1px solid',
                            borderColor: alpha(colors.warning.main, 0.2),
                          }}
                        >
                          <AlertTitle>Subscription Canceling</AlertTitle>
                          Your subscription is currently being cancelled. You cannot add new add-ons, but you can remove existing ones.
                          {subscriptionInfo?.order_number && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
                              Order: {subscriptionInfo.order_number}
                            </Typography>
                          )}
                        </Alert>
                      )}

                      {/* General Info Alert */}
                      <Alert
                        severity="info"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          backgroundColor: colors.info.background,
                          border: '1px solid',
                          borderColor: alpha(colors.info.main, 0.2),
                        }}
                      >
                        Add-ons will be charged to your next monthly payment. Some add-ons may require carrier verification.
                        {isSubscriptionCanceling && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: colors.warning.main }}>
                            Note: Your subscription is canceling. Please reactivate to add new add-ons.
                          </Typography>
                        )}
                      </Alert>

                      {managementError && (
                        <Alert
                          severity="error"
                          sx={{ mb: 2, borderRadius: 2 }}
                          action={
                            <Button
                              color="inherit"
                              size="small"
                              onClick={handleRetry}
                              startIcon={<RefreshCw size={16} />}
                            >
                              Retry
                            </Button>
                          }
                        >
                          {managementError}
                        </Alert>
                      )}

                      {planOffers.length === 0 && !managementLoading && (
                        <Box sx={{
                          textAlign: 'center',
                          py: 4
                        }}>
                          <PlusCircle
                            size={48}
                            style={{
                              color: colors.text.muted,
                              marginBottom: '16px',
                              opacity: 0.5
                            }}
                          />
                          <Typography sx={{ color: colors.text.secondary }}>
                            No add-ons available for this plan
                          </Typography>
                        </Box>
                      )}

                      {planOffers.map((addon) => {
                        const isToggling = featureToggling[addon.id];
                        const canAddThis = canModifyAddons && !isToggling;
                        const canRemoveThis = !isToggling; // Always allow removal

                        return (
                          <Box
                            key={addon.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 2,
                              mb: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: colors.border,
                              background: addon.enabled
                                ? alpha(colors.success.main, 0.05)
                                : 'transparent',
                              position: 'relative',
                              opacity: (!canAddThis && !addon.enabled) ? 0.6 : 1
                            }}
                          >
                            {/* Recommended/Popular badges */}
                            {(addon.isRecommended || addon.isPopular) && (
                              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                {addon.isRecommended && (
                                  <Chip
                                    size="small"
                                    icon={<Star size={10} />}
                                    label="Recommended"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.625rem',
                                      bgcolor: alpha(colors.warning.main, 0.1),
                                      color: colors.warning.main,
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                                {addon.isPopular && (
                                  <Chip
                                    size="small"
                                    icon={<TrendingUp size={10} />}
                                    label="Popular"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.625rem',
                                      bgcolor: alpha(colors.accent.pink.main, 0.1),
                                      color: colors.accent.pink.main,
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </Box>
                            )}

                            <Box sx={{ minWidth: 40, color: colors.text.primary, mr: 2 }}>
                              {addon.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{
                                fontWeight: 600,
                                color: colors.text.primary,
                                mb: 0.5
                              }}>
                                {addon.name}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.875rem',
                                color: colors.text.secondary
                              }}>
                                {addon.cost}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: colors.text.muted,
                                mt: 0.5
                              }}>
                                {addon.description}
                              </Typography>

                              {/* Additional info for active add-ons */}
                              {addon.enabled && addon.isInTrial && (
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  color: colors.info.main,
                                  mt: 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <Clock size={12} />
                                  Free trial active
                                </Typography>
                              )}

                              {addon.savings && (
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  color: colors.success.main,
                                  mt: 0.5
                                }}>
                                  {addon.savings}
                                </Typography>
                              )}

                              {addon.requiresVerification && (
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  color: colors.warning.main,
                                  mt: 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <Shield size={12} />
                                  Requires verification
                                </Typography>
                              )}
                            </Box>

                            {addon.toggleable && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isToggling && (
                                  <CircularProgress size={16} sx={{ color: colors.accent.pink.main }} />
                                )}
                                {addon.enabled ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Check size={16} />}
                                    onClick={() => handleFeatureToggle(addon.id, false)}
                                    disabled={!canRemoveThis}
                                    sx={{
                                      color: colors.success.main,
                                      borderColor: colors.success.main,
                                      '&:hover': {
                                        borderColor: colors.success.main,
                                        backgroundColor: alpha(colors.success.main, 0.05),
                                      }
                                    }}
                                  >
                                    Added
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PlusCircle size={16} />}
                                    onClick={() => handleFeatureToggle(addon.id, true)}
                                    disabled={!canAddThis}
                                    sx={{
                                      color: !canAddThis ? colors.text.muted : colors.accent.pink.main,
                                      borderColor: !canAddThis ? colors.text.muted : colors.accent.pink.main,
                                      '&:hover': {
                                        borderColor: !canAddThis ? colors.text.muted : colors.accent.pink.main,
                                        backgroundColor: !canAddThis
                                          ? 'transparent'
                                          : alpha(colors.accent.pink.main, 0.05),
                                      },
                                      '&.Mui-disabled': {
                                        opacity: 0.5,
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                    title={!canAddThis ? 'Cannot add add-ons to canceling subscription' : ''}
                                  >
                                    {addon.hasTrial && addon.trialDays ?
                                      `Try ${addon.trialDays} days free` : 'Add'
                                    }
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Tab 2: Circle Chat - UPDATED WITH PRIVACY */}
                  {activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                      {dynamicMembers && dynamicMembers.length > 0 ? (
                        dynamicMembers.map((member, index) => {
                          const memberStatus = getMemberStatus(member);
                          const displayName = getMemberDisplayName(member); // Use privacy-protected name
                          
                          return (
                            <Box
                              key={member.id || index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: colors.border,
                              }}
                            >
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: getChatStatusColor(memberStatus.status),
                                      border: '2px solid',
                                      borderColor: colors.background,
                                    }}
                                  />
                                }
                              >
                                <Avatar sx={{
                                  bgcolor: member.isAccountOwner
                                    ? '#FFD700'
                                    : member.isCurrentUser
                                      ? colors.accent.pink.main
                                      : colors.text.secondary,
                                  width: 40,
                                  height: 40,
                                  fontSize: '0.875rem',
                                  fontWeight: 600
                                }}>
                                  {getAvatarContent(member)}
                                </Avatar>
                              </Badge>
                              <Box sx={{ flex: 1, ml: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                  <Typography sx={{
                                    fontWeight: 600,
                                    color: colors.text.primary,
                                  }}>
                                    {displayName}
                                  </Typography>
                                  {member.isAccountOwner && (
                                    <Chip
                                      size="small"
                                      icon={<Crown size={10} />}
                                      label="Owner"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.625rem',
                                        bgcolor: alpha('#FFD700', 0.1),
                                        color: '#FFD700',
                                        border: '1px solid',
                                        borderColor: alpha('#FFD700', 0.3),
                                        fontWeight: 600
                                      }}
                                    />
                                  )}
                                  {member.isCurrentUser && (
                                    <Chip
                                      size="small"
                                      label="You"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.625rem',
                                        bgcolor: colors.accent.pink.main,
                                        color: 'white',
                                        fontWeight: 600
                                      }}
                                    />
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{
                                  color: member.isVerified ? colors.success.main : colors.warning.main,
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {member.isVerified ? (
                                    <>
                                      <Check size={12} style={{ marginRight: '4px' }} />
                                      Verified
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={12} style={{ marginRight: '4px' }} />
                                      Pending Verification
                                    </>
                                  )}
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<MessageCircle size={16} />}
                                sx={{
                                  color: colors.accent.pink.main,
                                  borderColor: colors.accent.pink.main,
                                  '&:hover': {
                                    borderColor: colors.accent.pink.main,
                                    backgroundColor: alpha(colors.accent.pink.main, 0.05),
                                  }
                                }}
                              >
                                Chat
                              </Button>
                            </Box>
                          );
                        })
                      ) : (
                        <Box sx={{
                          textAlign: 'center',
                          py: 4
                        }}>
                          <Users
                            size={48}
                            style={{
                              color: colors.text.muted,
                              marginBottom: '16px',
                              opacity: 0.5
                            }}
                          />
                          <Typography sx={{ color: colors.text.secondary }}>
                            No other members in this circle yet
                          </Typography>
                        </Box>
                      )}

                      {/* Chat Interface */}
                      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: colors.border }}>
                        <Typography sx={{ fontWeight: 600, mb: 2, color: colors.text.primary }}>
                          Group Chat
                        </Typography>

                        {/* Chat Messages */}
                        <Box
                          sx={{
                            height: '200px',
                            overflow: 'auto',
                            mb: 2,
                            border: '1px solid',
                            borderColor: colors.border,
                            borderRadius: 2,
                            p: 2,
                            background: alpha(colors.text.muted, 0.02)
                          }}
                        >
                          {chatMessages.length === 0 && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%'
                            }}>
                              <Typography sx={{ color: colors.text.muted }}>
                                No messages yet. Start the conversation!
                              </Typography>
                            </Box>
                          )}
                          {chatMessages.map((message, index) => {
                            // Apply privacy to chat message senders too
                            const senderDisplayName = message.isCurrentUser 
                              ? 'You' 
                              : message.isSystem 
                                ? 'System'
                                : getInitials(message.sender);
                                
                            return (
                              <Box
                                key={message.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  mb: 1,
                                  flexDirection: message.isCurrentUser ? 'row-reverse' : 'row'
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: message.isCurrentUser
                                      ? colors.accent.pink.main
                                      : message.isSystem
                                        ? colors.info.main
                                        : colors.text.secondary,
                                    fontSize: '0.625rem',
                                    fontWeight: 600
                                  }}
                                >
                                  {message.isSystem ? '🤖' : senderDisplayName.replace(/\./g, '')}
                                </Avatar>
                                <Box
                                  sx={{
                                    maxWidth: '70%',
                                    textAlign: message.isCurrentUser ? 'right' : 'left'
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: colors.text.muted,
                                      display: 'block',
                                      mb: 0.5
                                    }}
                                  >
                                    {senderDisplayName} • {message.timestamp.toLocaleTimeString()}
                                  </Typography>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1,
                                      borderRadius: 2,
                                      background: message.isCurrentUser
                                        ? colors.accent.pink.main
                                        : message.isSystem
                                          ? colors.info.background
                                          : alpha(colors.text.muted, 0.1),
                                      color: message.isCurrentUser
                                        ? 'white'
                                        : colors.text.primary,
                                      border: message.isCurrentUser ? 'none' : '1px solid',
                                      borderColor: colors.border
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {message.message}
                                    </Typography>
                                  </Paper>
                                </Box>
                              </Box>
                            );
                          })}
                          <div ref={chatEndRef} />
                        </Box>

                        {/* Chat Input */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            placeholder="Type your message..."
                            value={chatMessage}
                            onChange={(e) => setChatMessage(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleChatSend();
                              }
                            }}
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                          <Button
                            variant="contained"
                            onClick={handleChatSend}
                            disabled={!chatMessage.trim()}
                            sx={{
                              bgcolor: colors.accent.pink.main,
                              minWidth: 40,
                              '&:hover': {
                                bgcolor: colors.accent.pink.dark
                              }
                            }}
                          >
                            <Send size={16} />
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Tab 3: Support */}
                  {activeTab === 3 && (
                    <Box sx={{ p: 3 }}>
                      <Typography sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: colors.text.primary,
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        Circle Support Center
                        <Tooltip title="Get help with billing, service issues, and plan changes.">
                          <IconButton size="small" sx={{ ml: 1, color: colors.text.secondary }}>
                            <HelpCircle size={16} />
                          </IconButton>
                        </Tooltip>
                      </Typography>

                      <Alert
                        severity="info"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          backgroundColor: colors.info.background,
                          border: '1px solid',
                          borderColor: alpha(colors.info.main, 0.2),
                        }}
                      >
                        Our support team typically responds within 24 hours.
                      </Alert>

                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: colors.text.primary
                        }}>
                          Common Support Options
                        </Typography>
                        <Box sx={{
                          p: 2,
                          borderRadius: 3,
                          background: darkMode ? 'rgba(31, 41, 55, 0.3)' : 'rgba(249, 250, 251, 0.8)',
                          border: '1px solid',
                          borderColor: colors.border,
                        }}>
                          <List sx={{ p: 0 }}>
                            {[
                              { icon: <Phone />, text: "Number Transfer Issues", color: colors.secondary.main },
                              { icon: <Clock />, text: "Billing Questions", color: colors.warning.main },
                              { icon: <AlertTriangle />, text: "Service Interruptions", color: colors.error.main },
                              { icon: <PlusCircle />, text: "Add Feature Requests", color: colors.success.main }
                            ].map((item, index) => (
                              <ListItem
                                key={index}
                                button
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 2,
                                  mb: index < 3 ? 1 : 0,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: darkMode
                                      ? 'rgba(75, 85, 99, 0.2)'
                                      : 'rgba(243, 244, 246, 0.8)'
                                  }
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 36,
                                    color: item.color
                                  }}
                                >
                                  {React.cloneElement(item.icon, { size: 18 })}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.text}
                                  primaryTypographyProps={{
                                    fontWeight: 500,
                                    fontSize: '0.9375rem',
                                    color: colors.text.primary
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Box>

                      <Box sx={{
                        p: 0,
                        mb: 3,
                        position: 'relative'
                      }}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            textAlign: 'center',
                            background: alpha(colors.accent.pink.main, 0.05),
                            border: '1px solid',
                            borderColor: alpha(colors.accent.pink.main, 0.1),
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography sx={{
                              fontWeight: 700,
                              color: colors.accent.pink.main,
                              mb: 1,
                              fontSize: '1.125rem'
                            }}>
                              Need Immediate Assistance?
                            </Typography>
                            <Typography sx={{
                              color: colors.text.secondary,
                              mb: 2.5,
                              fontSize: '0.9375rem',
                              maxWidth: '90%',
                              mx: 'auto'
                            }}>
                              Our support team is available to help with any issues related to your circle.
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<HelpCircle size={18} />}
                              onClick={() => setShowSupportDialog(true)}
                              sx={{
                                bgcolor: colors.accent.pink.main,
                                color: 'white',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '12px',
                                px: 3,
                                py: 1.25,
                                '&:hover': {
                                  bgcolor: colors.accent.pink.dark
                                }
                              }}
                            >
                              Request Support
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1.5, color: colors.text.primary }}>
                          Account & Payment
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                          flexWrap: 'wrap',
                          gap: 2
                        }}>
                          {[
                            { icon: <Calendar size={16} />, text: "Billing History" },
                            { icon: <Bell size={16} />, text: "Notifications" }
                          ].map((item, index) => (
                            <Button
                              key={index}
                              variant="outlined"
                              startIcon={item.icon}
                              sx={{
                                flex: '1 1 calc(50% - 8px)',
                                color: colors.text.primary,
                                borderColor: colors.border,
                                textTransform: 'none',
                                fontWeight: 500,
                                justifyContent: 'flex-start',
                                px: 2,
                                py: 1.25,
                                borderRadius: '12px',
                                background: darkMode
                                  ? 'rgba(31, 41, 55, 0.3)'
                                  : 'rgba(249, 250, 251, 0.7)',
                                '&:hover': {
                                  background: darkMode
                                    ? 'rgba(31, 41, 55, 0.4)'
                                    : 'rgba(249, 250, 251, 0.9)',
                                  borderColor: darkMode
                                    ? 'rgba(75, 85, 99, 0.5)'
                                    : 'rgba(209, 213, 219, 0.8)'
                                }
                              }}
                            >
                              {item.text}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </Box>

          {/* Action Footer */}
          <Box sx={{
            mt: 'auto',
            display: 'flex',
            borderTop: '1px solid',
            borderColor: colors.border
          }}>
            <Box component="button" sx={{
              width: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              color: colors.primary.main,
              bgcolor: colors.paper.bg,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              outline: 'none',
              '&:hover': {
                bgcolor: alpha(colors.primary.main, 0.05)
              }
            }}
              onClick={() => {
                setShowSupportDialog(true);
                trackInteraction('request_support_footer');
              }}
            >
              Request Support
            </Box>
            <Button
              fullWidth
              sx={{
                width: '50%',
                py: 2,
                color: 'white',
                bgcolor: colors.accent.pink.main,
                fontWeight: 600,
                borderRadius: 0,
                textTransform: 'uppercase',
                '&:hover': {
                  bgcolor: colors.accent.pink.dark
                }
              }}
              onClick={() => trackInteraction('manage_subscription')}
            >
              Manage Subscription
            </Button>
          </Box>
        </Box>

        {/* Right Column - Members List - UPDATED WITH PRIVACY */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            height: 'fit-content',
            background: colors.paper.bg,
            border: `1px solid ${colors.paper.border}`,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mb: 3,
                color: colors.text.primary,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Users size={18} style={{ color: colors.accent.pink.main }} />
              {circleName} - Circle Members
            </Typography>

            {/* Members List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {membersLoadingState || membersLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  ))}
                </Box>
              ) : membersErrorState || membersError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {membersErrorState || membersError}
                </Alert>
              ) : dynamicMembers && dynamicMembers.length > 0 ? (
                dynamicMembers.map((member, index) => {
                  const displayName = getMemberDisplayName(member);
                  
                  return (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        border: '1px solid',
                        borderColor: member.isCurrentUser
                          ? alpha(colors.accent.pink.main, 0.15)
                          : colors.paper.border,
                        background: member.isCurrentUser
                          ? alpha(colors.accent.pink.main, 0.05)
                          : colors.paper.bg,
                      }}
                    >
                      <Avatar sx={{
                        bgcolor: member.isCurrentUser
                          ? colors.accent.pink.main
                          : member.isAccountOwner
                            ? '#FFD700'
                            : colors.text.secondary,
                        width: 40,
                        height: 40,
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>
                        {getAvatarContent(member)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{
                              color: colors.text.primary
                            }}
                          >
                            {displayName}
                          </Typography>

                          {member.isAccountOwner && (
                            <Chip
                              icon={<Crown size={10} />}
                              label="Owner"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.625rem',
                                bgcolor: alpha('#FFD700', 0.1),
                                color: '#FFD700',
                                border: '1px solid',
                                borderColor: alpha('#FFD700', 0.3),
                                fontWeight: 600
                              }}
                            />
                          )}

                          {member.isCurrentUser && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.625rem',
                                bgcolor: colors.accent.pink.main,
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: member.isVerified
                              ? colors.success.main
                              : colors.warning.main,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {member.isVerified ? (
                            <>
                              <Check size={12} style={{ marginRight: '4px' }} />
                              Verified
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={12} style={{ marginRight: '4px' }} />
                              Pending Verification
                            </>
                          )}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          color: alpha(colors.text.primary, 0.7),
                          background: alpha(colors.text.secondary, 0.1),
                          border: '1px solid',
                          borderColor: colors.border,
                          '&:hover': {
                            background: alpha(colors.text.secondary, 0.2),
                          }
                        }}
                        title={`Message ${displayName}`}
                      >
                        <MessageCircle size={16} />
                      </IconButton>
                    </Paper>
                  );
                })
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  borderRadius: 3,
                  background: alpha(colors.text.muted, 0.05),
                  border: '1px solid',
                  borderColor: colors.border,
                }}>
                  <Users
                    size={48}
                    style={{
                      color: colors.text.muted,
                      marginBottom: '16px',
                      opacity: 0.5
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={colors.text.muted}
                  >
                    No members found for this circle
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Back Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowLeft size={16} />}
                onClick={() => {
                  onBackToCircles();
                  trackInteraction('back_to_circles_footer');
                }}
                sx={{
                  borderRadius: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: colors.text.secondary,
                  borderColor: colors.border,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    borderColor: colors.text.secondary,
                    bgcolor: alpha(colors.text.secondary, 0.05)
                  }
                }}
              >
                Back to Circles
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Support Ticket Dialog */}
      <Dialog
        open={showSupportDialog}
        onClose={() => !supportSubmitting && setShowSupportDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: colors.paper.bg,
            border: `1px solid ${colors.paper.border}`,
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          color: colors.text.primary,
          fontWeight: 600
        }}>
          Request Support
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
            Please provide details about your issue and our support team will respond as soon as possible.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            fullWidth
            variant="outlined"
            value={supportTopic}
            onChange={(e) => setSupportTopic(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.text.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.accent.pink.main,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.accent.pink.main,
              },
            }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.text.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.accent.pink.main,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.accent.pink.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowSupportDialog(false)}
            disabled={supportSubmitting}
            sx={{ color: colors.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSupportSubmit}
            variant="contained"
            disabled={!supportTopic || !supportMessage || supportSubmitting}
            startIcon={supportSubmitting ? <CircularProgress size={16} /> : <Send size={16} />}
            sx={{
              bgcolor: colors.accent.pink.main,
              '&:hover': {
                bgcolor: colors.accent.pink.dark
              },
              '&.Mui-disabled': {
                bgcolor: alpha(colors.text.muted, 0.2),
                color: alpha(colors.text.muted, 0.5),
              }
            }}
          >
            {supportSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feature Dialog */}
      <Dialog
        open={showFeatureDialog}
        onClose={() => setShowFeatureDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: colors.paper.bg,
            border: `1px solid ${colors.paper.border}`,
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          color: colors.text.primary,
          fontWeight: 600
        }}>
          {selectedFeature?.name}
        </DialogTitle>
        <DialogContent>
          {selectedFeature && (
            <>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(colors.text.muted, 0.05),
              }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: alpha(colors.text.muted, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  color: colors.text.primary
                }}>
                  {selectedFeature.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: colors.text.primary }}>
                    {selectedFeature.name}
                  </Typography>
                  {selectedFeature.cost && (
                    <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                      {selectedFeature.cost}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
                {selectedFeature.fullDescription || selectedFeature.description}
              </Typography>

              {selectedFeature.usage && (
                <Typography variant="body2" sx={{ color: colors.info.main, mb: 2 }}>
                  <strong>Current Usage:</strong> {selectedFeature.usage}
                </Typography>
              )}

              {selectedFeature.savings && (
                <Typography variant="body2" sx={{ color: colors.success.main, mb: 2 }}>
                  <strong>Savings:</strong> {selectedFeature.savings}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowFeatureDialog(false)}
            sx={{ color: colors.text.secondary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{
            borderRadius: 3,
            minWidth: 300,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Feedback Toast */}
      <AnimatePresence>
        {isFeedbackVisible && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.8 }}
            style={{
              position: 'fixed',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 4,
                py: 2,
                borderRadius: 4,
                background: feedbackType === 'success'
                  ? colors.success.main
                  : feedbackType === 'error'
                    ? colors.error.main
                    : colors.info.main,
                color: 'white',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }}
            >
              {feedbackType === 'success' && <Check size={16} />}
              {feedbackType === 'error' && <AlertTriangle size={16} />}
              {feedbackType === 'info' && <Bell size={16} />}
              {feedbackMessage}
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CircleTabDetailView;